import { MB_utilHooks } from '@/utils/general/util-hooks';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { Dimensions, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { MB_Modal, MB_ModalAnimations } from '@/components/general/modal';

interface MB_ModalDropdownProps {
  /**
   * Whether or not the dropdown is visible.
   */
  visible: boolean;

  /**
   * Children components.
   */
  children: ReactNode;

  /**
   * Component's style.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * The x position to start the dropdown from.
   * Generally the x position of the parent component the
   * dropdown is coming from.
   */
  callerX: number;

  /**
   * The y position to start the dropdown from.
   * Generally the y position of the parent component the
   * dropdown is coming from.
   */
  callerY: number;

  /**
   * The width of the parent component the dropdown is coming
   * from. This will add some space to the left of the dropdown.
   */
  callerWidth: number;

  /**
   * The height of the parent component the dropdown is coming
   * from. This will add some space to the top of the dropdown.
   */
  callerHeight: number;

  /**
   * Callback function called when the dropdown is dismissed.
   */
  onClose?: () => void;

  /**
   * The animation to use when showing the dropdown.
   *
   * Default: 'fadeIn'
   */
  animationIn?: MB_ModalAnimations;

  /**
   * The animation to use when closing the dropdown.
   *
   * Default: 'fadeOut'
   */
  animationOut?: MB_ModalAnimations;
}

const MB_ModalDropdown = (props: MB_ModalDropdownProps) => {
  const { visible, children, style, callerX, callerY, callerWidth, callerHeight, onClose, animationIn = 'fadeIn', animationOut = 'fadeOut' } = props;

  const [size, setSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<View | null>(null);

  MB_utilHooks.useWidthChange(
    visible,
    useCallback(() => visible && onClose?.(), [onClose, visible])
  );

  const modalPosition = () => {
    const dimensions = Dimensions.get('window');
    const windowWidth = dimensions.width;
    const windowHeight = dimensions.height;
    const modalHeight = size.height || (33 + StyleSheet.hairlineWidth) * 5;
    const bottomSpace = windowHeight - callerY - callerHeight;
    const rightSpace = windowWidth - callerX;
    const showInBottom = bottomSpace >= modalHeight || bottomSpace >= callerY;
    const showInLeft = rightSpace >= callerX;
    const position: {
      height?: number | string;
      top?: number;
      left?: number;
      width?: number | string;
      right?: number;
    } = {
      height: modalHeight,
      top: showInBottom ? callerY + callerHeight : Math.max(0, callerY - Number(modalHeight)),
    };
    if (showInLeft) {
      position.left = callerX;
    } else {
      const modalWidth = size.width || -1;
      if (modalWidth !== -1) {
        position.width = modalWidth;
      }
      position.right = rightSpace - callerWidth;
    }
    return position;
  };

  const onShow = useCallback(() => {
    const flattenStyle = StyleSheet.flatten(style);
    const styleWidth = flattenStyle?.width as number | undefined;
    const styleHeight = flattenStyle?.height as number | undefined;
    containerRef.current?.measure((_, __, width, height) => setSize({ width: styleWidth ?? width, height: styleHeight ?? height }));
  }, [style]);

  return (
    <MB_Modal
      isVisible={visible}
      onDismiss={onClose}
      disableBackdropBlur
      closeOnBackdropPress
      hideCloseButton
      animationIn={animationIn}
      animationOut={animationOut}
      simplified
      onShow={onShow}
    >
      <View style={[style, { position: 'absolute' }, modalPosition()]}>
        <View ref={containerRef}>{children}</View>
      </View>
    </MB_Modal>
  );
};

export { MB_ModalDropdown };
